import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "@phosphor-icons/react";
import { Reveal } from "@/components/Reveal";
import { CALENDLY_URL } from "@/constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  name: "",
  email: "",
  interest: "",
  message: "",
  arr_band: "",
  need: "",
  team: "",
  timeline: "",
};

const ARR_BANDS = ["Pre-revenue – $1M ARR", "$1M – $10M ARR", "$10M – $50M ARR", "$50M+ ARR"];
const NEEDS = ["Enterprise validation", "GTM assets & blueprints", "Board-level alignment", "Not sure yet"];
const TEAM_OPTIONS = ["Yes — a team is in place to execute", "Partially", "Not yet"];
const TIMELINES = ["This quarter", "This half", "Exploring"];

const inputClass =
  "w-full bg-transparent border-0 border-b border-white/20 focus:border-[#E6E4D9] focus:outline-none focus:ring-0 rounded-none px-0 py-3 text-white placeholder:text-white/30 transition-colors duration-300";

const selectClass = `${inputClass} appearance-none cursor-pointer [&>option]:bg-[#0A0A0A]`;

const SelectField = ({ id, testId, label, value, onChange, options, required }) => (
  <div>
    <label
      htmlFor={id}
      className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 block mb-1"
    >
      {label}
    </label>
    <select
      id={id}
      data-testid={testId}
      value={value}
      onChange={onChange}
      required={required}
      className={selectClass}
    >
      <option value="" disabled>
        Select…
      </option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  </div>
);

export default function ContactFooter() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const update = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success(
        "Enquiry received. I personally respond within one business day."
      );
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error(
        "Something went wrong. Please try again or book directly via the strategy call link."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section
        id="contact"
        data-testid="contact-section"
        className="px-6 lg:px-12 py-24 lg:py-32"
      >
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-6">
              07 — Engage
            </p>
            <h2 className="font-serif tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.1]">
              If the motion is stuck in{" "}
              <span className="italic text-[#E6E4D9]">the rooms that matter.</span>
            </h2>
            <p className="mt-8 max-w-lg text-base sm:text-lg font-light leading-relaxed text-white/70">
              Engagements are selective and limited — a team to execute is
              required. Investment is scoped after the briefing; the work is
              sprints and retained counsel, not hours.
            </p>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-book-call-button"
              className="group mt-10 inline-flex items-center gap-3 bg-[#E6E4D9] text-black font-mono text-xs sm:text-sm uppercase tracking-[0.15em] px-8 py-4 hover:bg-[#C2C0B6] transition-colors duration-300"
            >
              Book a Strategy Call
              <ArrowUpRight
                size={16}
                weight="bold"
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
            <p
              data-testid="cta-expectation-line"
              className="mt-6 max-w-md font-mono text-[10px] uppercase tracking-[0.2em] leading-relaxed text-white/40"
            >
              30-minute fit assessment. We identify the must-win room, the
              technical objection, the decision-maker, and the smallest
              engagement that creates evidence.
            </p>
            <p className="mt-8 font-mono text-xs tracking-[0.15em] text-white/50">
              Prefer email?{" "}
              <a
                href="mailto:info@teccexec.com"
                data-testid="contact-email-link"
                className="text-[#E6E4D9] hover:text-white transition-colors duration-300"
              >
                info@teccexec.com
              </a>
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <form
              data-testid="contact-enquiry-form"
              onSubmit={handleSubmit}
              className="flex flex-col gap-8 lg:pt-16"
            >
              <div>
                <label
                  htmlFor="contact-name"
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 block mb-1"
                >
                  Name
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  type="text"
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your full name"
                  className={inputClass}
                />
              </div>
              <div>
                <label
                  htmlFor="contact-email"
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 block mb-1"
                >
                  Work Email
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@company.com"
                  className={inputClass}
                />
              </div>
              <div>
                <label
                  htmlFor="contact-interest"
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 block mb-1"
                >
                  Area of Interest
                </label>
                <input
                  id="contact-interest"
                  data-testid="contact-interest-input"
                  type="text"
                  value={form.interest}
                  onChange={update("interest")}
                  placeholder="Field CTO, GTM assets, governance..."
                  className={inputClass}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8">
                <SelectField
                  id="contact-arr-band"
                  testId="contact-arr-band-select"
                  label="Company Stage / ARR Band"
                  value={form.arr_band}
                  onChange={update("arr_band")}
                  options={ARR_BANDS}
                />
                <SelectField
                  id="contact-need"
                  testId="contact-need-select"
                  label="Primary Need"
                  value={form.need}
                  onChange={update("need")}
                  options={NEEDS}
                />
                <SelectField
                  id="contact-team"
                  testId="contact-team-select"
                  label="Execution Team In Place"
                  value={form.team}
                  onChange={update("team")}
                  options={TEAM_OPTIONS}
                  required
                />
                <SelectField
                  id="contact-timeline"
                  testId="contact-timeline-select"
                  label="Timeline"
                  value={form.timeline}
                  onChange={update("timeline")}
                  options={TIMELINES}
                />
              </div>
              <div>
                <label
                  htmlFor="contact-message"
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 block mb-1"
                >
                  Technical GTM Bottleneck
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  rows={3}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="The pursuit, objection, or architecture decision you're facing"
                  className={`${inputClass} resize-none`}
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={submitting}
                className="self-start inline-flex items-center gap-3 border border-white/25 text-white font-mono text-xs uppercase tracking-[0.15em] px-8 py-4 hover:bg-white hover:text-black transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending..." : "Send Enquiry"}
                <ArrowUpRight size={14} weight="bold" />
              </button>
              <p className="font-mono text-[10px] tracking-[0.12em] text-white/30 -mt-4">
                Your details are used only to respond to your enquiry. No lists, no sharing.
              </p>
            </form>
          </Reveal>
        </div>
      </section>

      <footer
        data-testid="site-footer"
        className="border-t border-white/10 px-6 lg:px-12 py-8"
      >
        <div className="max-w-[1440px] mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
              © 2026 Tech Executive Capability Center LLC
            </p>
            <a
              href="/privacy"
              data-testid="footer-privacy-link"
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 hover:text-white transition-colors duration-300"
            >
              Privacy Policy
            </a>
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
            Enterprise GTM &amp; Technical Acceleration
          </p>
        </div>
      </footer>
    </>
  );
}
